// src/components/ContactUs.jsx
import { useState } from "react";
import axios from "axios";
import { Form, Button, Modal, Row, Col } from "react-bootstrap";
import { Phone, Mail, MapPin } from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";
import ContactBG from "../assets/backgroundImage.jpg";

const RECAPTCHA_SITE_KEY = "6Lfe9rgrAAAAAJxXrXu5WiBhffazdcJuZhBzGJBY"; // ok to be public

export default function ContactUs() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalSuccess, setModalSuccess] = useState(true);
  const [modalMessage, setModalMessage] = useState("");

  // reCAPTCHA
  const [captchaToken, setCaptchaToken] = useState(null);
  const [recaptchaRef, setRecaptchaRef] = useState(null);

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = "Your name is required.";
    if (!email.trim()) e.email = "Your email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      e.email = "Enter a valid email.";
    if (!message.trim()) e.message = "Please enter a message.";
    if (!captchaToken) e.captcha = "Please complete the captcha.";
    setErrors(e);
    return e;
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    const e = validate();
    if (Object.keys(e).length) return;

    try {
      setSubmitting(true);

      // ⬇️ SEND THE CAPTCHA TOKEN TOO
      const res = await axios.post(
        "https://allwellcompanionhomecare.com/api/contact-create.php",
        { name, email, message, recaptchaToken: captchaToken },
        { headers: { "Content-Type": "application/json" }, timeout: 20000 }
      );

      if (!res.data?.success)
        throw new Error(res.data?.message || "Server error");

      setModalSuccess(true);
      setModalMessage(
        "✅ Message sent successfully! We’ll get back to you shortly."
      );
      setModalOpen(true);
      setName("");
      setEmail("");
      setMessage("");
      setErrors({});
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Unknown error";
      console.error("Contact API error:", err);
      setModalSuccess(false);
      setModalMessage(`❌ Failed to send message. ${msg}`);
      setModalOpen(true);
    } finally {
      setSubmitting(false);
      if (recaptchaRef) recaptchaRef.reset();
      setCaptchaToken(null);
    }
  };

  const cards = [
    {
      icon: <Phone className="w-7 h-7" />,
      title: "Call Us",
      text: "630.339.9000",
      href: "tel:16303399000",
    },
    {
      icon: <Mail className="w-7 h-7" />,
      title: "Email Us",
      text: "info@allwellcompanionhomecare.com",
      href: "mailto:info@allwellcompanionhomecare.com",
    },
    {
      icon: <MapPin className="w-7 h-7" />,
      title: "Visit Us",
      text: "Schaumburg, Illinois",
      href: "https://maps.google.com/?q=Schaumburg,+Illinois",
    },
  ];

  return (
    <section
      id="contact"
      className="relative w-full bg-cover bg-center py-16 scroll-mt-[120px]"
      style={{ backgroundImage: `url(${ContactBG})` }}
    >
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative max-w-6xl mx-auto px-6 text-white">
        <h2 className="text-4xl font-bold text-center">Contact Us</h2>
        <p className="text-center text-gray-200 mt-3 max-w-3xl mx-auto">
          We are here to help! Reach out for inquiries or to schedule a free
          assessment.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
          {cards.map((c) => (
            <a
              key={c.title}
              href={c.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative rounded-2xl bg-gray-800/70 text-white p-6 text-center shadow-md transition duration-300 hover:-translate-y-1.5 hover:shadow-2xl hover:bg-gradient-to-r hover:from-teal-600 hover:to-emerald-600"
            >
              <div className="flex flex-col items-center gap-3">
                <div className="text-pink-400 group-hover:text-white transition">
                  {c.icon}
                </div>
                <h3 className="text-xl font-semibold">{c.title}</h3>
                <p className="text-sm opacity-95">{c.text}</p>
              </div>
              <span className="absolute bottom-0 left-0 right-0 h-1 bg-teal-400/90 opacity-0 group-hover:opacity-100 transition" />
            </a>
          ))}
        </div>

        <div className="max-w-3xl mx-auto bg-white text-gray-900 rounded-2xl shadow-xl mt-12">
          <div className="px-6 py-6 sm:px-8 sm:py-8">
            <h3 className="text-2xl font-semibold text-center mb-4">
              Send Us a Message
            </h3>

            <Form noValidate onSubmit={handleSubmit}>
              <Row className="g-3">
                <Col xs={12}>
                  <Form.Group controlId="contactName">
                    <Form.Label>
                      Your Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="First and last name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      isInvalid={!!errors.name}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.name}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col xs={12}>
                  <Form.Group controlId="contactEmail">
                    <Form.Label>
                      Your Email <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      isInvalid={!!errors.email}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col xs={12}>
                  <Form.Group controlId="contactMessage">
                    <Form.Label>
                      Your Message <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      placeholder="Write your message here"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      isInvalid={!!errors.message}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <div className="mt-3">
                <ReCAPTCHA
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={(token) => {
                    setCaptchaToken(token);
                    setErrors((p) => ({ ...p, captcha: "" }));
                  }}
                  onExpired={() => setCaptchaToken(null)}
                  ref={(r) => setRecaptchaRef(r)}
                />
                {errors.captcha && (
                  <div className="text-danger small mt-1">{errors.captcha}</div>
                )}
              </div>

              <div className="mt-4 text-center">
                <Button
                  type="submit"
                  variant="success"
                  className="px-5"
                  disabled={submitting || !captchaToken} // ⬅️ block until captcha solved
                >
                  {submitting ? "Sending..." : "Send Message"}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>

      <Modal show={modalOpen} onHide={() => setModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{modalSuccess ? "Success" : "Error"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalMessage}</Modal.Body>
        <Modal.Footer>
          <Button
            variant={modalSuccess ? "success" : "secondary"}
            onClick={() => setModalOpen(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
}
